import { useState, useEffect, useCallback } from 'react';
import { useMessenger } from '../context/messengerContext';

/**
 * Универсальный хук установки PWA / добавления на главный экран
 * для Telegram, MAX и обычного web.
 *
 * Возвращает:
 *  - canInstall: boolean — нужно ли показывать кнопку
 *  - install: () => void — обработчик клика по кнопке
 */
export function usePWAInstall() {
    const { platform, openLink, userId } = useMessenger();
    const [canInstall, setCanInstall] = useState(false);

    // Telegram / MAX: показываем кнопку, если приложение ещё не установлено как PWA
    useEffect(() => {
        if (platform === 'telegram' || platform === 'max') {
            const standalone = window.matchMedia('(display-mode: standalone)').matches
                || window.navigator.standalone === true;
            setCanInstall(!standalone);
        }
    }, [platform]);

    // Обычный web: используем beforeinstallprompt, перехваченный в index.js
    useEffect(() => {
        if (platform !== 'web') return;

        const updateStateFromWindow = () => {
            const hasPrompt = Boolean(window.__pwaDeferredPrompt);
            setCanInstall(hasPrompt);
        };

        updateStateFromWindow();

        const handleBefore = () => {
            updateStateFromWindow();
        };
        const handleInstalled = () => {
            // После установки скрываем кнопку
            setCanInstall(false);
        };

        window.addEventListener('pwa-beforeinstallprompt', handleBefore);
        window.addEventListener('pwa-appinstalled', handleInstalled);

        return () => {
            window.removeEventListener('pwa-beforeinstallprompt', handleBefore);
            window.removeEventListener('pwa-appinstalled', handleInstalled);
        };
    }, [platform]);

    const install = useCallback(() => {
        if (!canInstall) return;

        if (platform === 'telegram' || platform === 'max') {
            // Открываем страницу установки во внешнем браузере
            // openLink автоматически добавит tg_id / max_id в URL
            if (openLink && typeof window !== 'undefined') {
                openLink(`${window.location.origin}/install`);
            }
            return;
        }

        if (platform === 'web') {
            const promptEvent = window.__pwaDeferredPrompt;
            if (promptEvent?.prompt) {
                promptEvent.prompt();
                // После вызова prompt событие обычно больше не нужно
                window.__pwaDeferredPrompt = null;
                setCanInstall(false);
            }
            return;
        }
    }, [canInstall, platform, openLink, userId]);

    return { canInstall, install };
}
